import type { AcademyLearnerSearchResult, CommunityLearnerSearchResult } from '../../types';
import { useState } from 'react';
import { Divider, ListBox, Popover, SearchField, Span, Stack, SubHeading } from '@workramp/ui';
import { useDebouncedValue } from '~/hooks';
import { APP_CONSTANTS, getExternalUser } from '~/common/explicit-exports';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { getCommunitiesViewUrl } from '~/clc/features/community/learner/utils/paths';
import {
  createAcademyAISearchAnswer,
  formatAlgoliaHitForHubResult,
  useAlgoliaHubSearch,
} from '~/clc/features/clc-hub-learner-search';
import { getAcademiesViewTarget, getAcademiesViewUrl } from '~/clc/features/academies/utils';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { LearnerAIQuestionAnswer } from '~/elc/features/learner-search/components/LearnerAIQuestionAnswer';
import { useAIAnswerWithSubscription } from '~/elc/features/learner-search/hooks';
import { StyledHighlightSpan, StyledP } from './HubLearnerSearchBar.styles';

const { I18n } = window;

const ALGOLIA_SEARCH_DEBOUNCE_MS = 300;
const DEDICATED_LEARNER_SEARCH_PAGE_ENABLED = isFeatureEnabled('hub-dedicated-learner-search');

export const HubLearnerSearchBar = ({
  algoliaSearchApiKey,
  algoliaAppId,
  academyContentTypesToLocalizedString,
  communityContentTypesToLocalizedString,
  prioritizeAcademies,
  className,
  aiSearchAnswersEnabled,
}: {
  className?: string;
  algoliaSearchApiKey: string;
  algoliaAppId: string;
  academyContentTypesToLocalizedString: Record<string, string>;
  communityContentTypesToLocalizedString: Record<string, string>;
  prioritizeAcademies?: boolean;
  aiSearchAnswersEnabled?: boolean;
}) => {
  const [query, setQuery] = useState('');
  const [openResults, setOpenResults] = useState(false);
  const TRANSLATED_SEARCH = I18n.t('common.community.navigation.search.search');
  const TRANSLATED_SEARCH_RESULTS = I18n.t('common.community.navigation.search.search_results');
  const TRANSLATED_NO_RESULTS_FOUND = I18n.t('common.community.navigation.search.no_results_found');

  const debouncedSearchQuery = useDebouncedValue(query, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const debouncedAIAnswerSearchQuery = useDebouncedValue(query, 1000);
  const { isInitialLoading, data } = useAlgoliaHubSearch({
    algoliaAppId,
    algoliaSearchApiKey,
    value: debouncedSearchQuery,
  });
  const userId = getExternalUser()?.id;

  const {
    loading: answerLoading,
    answer,
    content: answerContent,
    progressMessage,
  } = useAIAnswerWithSubscription({
    question: debouncedAIAnswerSearchQuery,
    userId,
    apiCall: createAcademyAISearchAnswer,
    enabled: aiSearchAnswersEnabled,
    channelParams: { for_academy: true },
    minQuestionLength: 5,
  });

  let results: (CommunityLearnerSearchResult | AcademyLearnerSearchResult)[] = [];

  if (isInitialLoading || !debouncedSearchQuery) {
    results = [];
  } else if (data?.length) {
    results = data.map(formatAlgoliaHitForHubResult);
  }

  const getListItemForTopic = (result: CommunityLearnerSearchResult) => {
    const { id, snippetResult, resultTitle } = result;

    return (
      <ListBox.Item key={id} href={getCommunitiesViewUrl(result)} textValue={resultTitle}>
        <StyledHighlightSpan
          dangerouslySetInnerHTML={{
            __html: sanitizeUserInput(snippetResult.title.value, { allowSafeTags: true }),
          }}
        />
        <StyledP size={1}>
          <StyledHighlightSpan
            dangerouslySetInnerHTML={{
              __html: sanitizeUserInput(snippetResult.description?.value || '', { allowSafeTags: true }),
            }}
          />
        </StyledP>
      </ListBox.Item>
    );
  };

  const getListItemForPost = (result: CommunityLearnerSearchResult) => {
    const { id, lastUpdated, topicName, snippetResult, resultTitle } = result;

    return (
      <ListBox.Item key={id} href={getCommunitiesViewUrl(result)} textValue={resultTitle}>
        <StyledHighlightSpan
          dangerouslySetInnerHTML={{
            __html: sanitizeUserInput(snippetResult.title.value, { allowSafeTags: true }),
          }}
        />
        <StyledP size={1}>
          <Span weight="medium">{topicName}</Span> · {lastUpdated} ·
          <StyledHighlightSpan
            dangerouslySetInnerHTML={{
              __html: sanitizeUserInput(snippetResult.content.value, { allowSafeTags: true }),
            }}
          />
        </StyledP>
      </ListBox.Item>
    );
  };

  const getListItemForComment = (result: CommunityLearnerSearchResult) => {
    const { id, lastUpdated, topicName, snippetResult, resultTitle } = result;
    return (
      <ListBox.Item key={id} href={getCommunitiesViewUrl(result)} textValue={resultTitle}>
        <StyledHighlightSpan
          dangerouslySetInnerHTML={{
            __html: sanitizeUserInput(snippetResult.content.value, { allowSafeTags: true }),
          }}
        />
        <StyledP size={1}>
          <Span weight="medium">{topicName}</Span> · {lastUpdated}
        </StyledP>
      </ListBox.Item>
    );
  };

  const getListItemForAcademyContent = (result: AcademyLearnerSearchResult) => {
    const { id, contentId, slug, snippetResult, resultTitle, parentName, parentId, type, contentType } = result;

    return (
      <ListBox.Item
        key={id}
        href={getAcademiesViewUrl({
          type,
          id,
          parentContentId: parentId,
          contentId,
          contentType,
          slug,
        })}
        textValue={resultTitle}
        target={getAcademiesViewTarget(type, contentType)}
      >
        <StyledHighlightSpan
          dangerouslySetInnerHTML={{
            __html: sanitizeUserInput(snippetResult.name?.value, {
              allowSafeTags: true,
            }),
          }}
        />
        <StyledP size={1}>
          <StyledHighlightSpan
            dangerouslySetInnerHTML={{
              __html: sanitizeUserInput(snippetResult.description?.value, { allowSafeTags: true }),
            }}
          />
        </StyledP>
        {parentName && (
          <StyledP size={1}>
            <Span
              weight="medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeUserInput(parentName, { allowSafeTags: true }),
              }}
            />
          </StyledP>
        )}
      </ListBox.Item>
    );
  };

  const reset = () => {
    setQuery('');
    setOpenResults(false);
  };

  function redirectToDedicatedLearnerSearchPage() {
    window.location.href = `${window.location.origin}/advanced_search?seachTerm=${query}&originProduct=${
      prioritizeAcademies ? 'academy' : 'community'
    }`;
  }

  const formattedAcademyResults: Record<string, any[]> = {};
  const formattedCommunityResults: Record<string, any[]> = {};
  results.forEach((res) => {
    const { type } = res;
    const contentType = 'contentType' in res ? res.contentType : null;
    let formattedType: string;
    let formattedRes;

    if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_TOPIC) {
      formattedRes = getListItemForTopic(res as CommunityLearnerSearchResult);
    } else if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_POST) {
      formattedRes = getListItemForPost(res as CommunityLearnerSearchResult);
    } else if (type === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_COMMENT) {
      formattedRes = getListItemForComment(res as CommunityLearnerSearchResult);
    } else if (type) {
      formattedRes = getListItemForAcademyContent(res as AcademyLearnerSearchResult);
    }

    if (contentType && academyContentTypesToLocalizedString[contentType]) {
      formattedType = academyContentTypesToLocalizedString[contentType];
      if (!formattedAcademyResults[formattedType]) formattedAcademyResults[formattedType] = [];
      formattedAcademyResults[formattedType].push(formattedRes);
    } else if (academyContentTypesToLocalizedString[type]) {
      formattedType = academyContentTypesToLocalizedString[type];
      if (!formattedAcademyResults[formattedType]) formattedAcademyResults[formattedType] = [];
      formattedAcademyResults[formattedType].push(formattedRes);
    } else if (communityContentTypesToLocalizedString[type]) {
      formattedType = communityContentTypesToLocalizedString[type];
      if (!formattedCommunityResults[formattedType]) formattedCommunityResults[formattedType] = [];
      formattedCommunityResults[formattedType].push(formattedRes);
    }
  });

  const orderedResults = prioritizeAcademies
    ? { ...formattedAcademyResults, ...formattedCommunityResults }
    : { ...formattedCommunityResults, ...formattedAcademyResults };

  return (
    <div className={className}>
      <Popover
        isNonModal
        title="Search"
        width={500}
        placement="bottom-end"
        onOpenChange={setOpenResults}
        isOpen={openResults && query.length > 0 && !isInitialLoading}
        buttonEl={
          <SearchField
            hideLabel
            label="Search"
            placeholder={TRANSLATED_SEARCH}
            value={query}
            onChange={setQuery}
            onInput={() => setOpenResults(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && DEDICATED_LEARNER_SEARCH_PAGE_ENABLED) {
                redirectToDedicatedLearnerSearchPage();
              }
            }}
          />
        }
      >
        {userId && aiSearchAnswersEnabled && debouncedSearchQuery?.length > 5 && (answerLoading || answer) && (
          <Stack size={2}>
            <SubHeading level="md" color="secondary">
              AI ANSWER
            </SubHeading>
            <LearnerAIQuestionAnswer
              loading={answerLoading}
              answer={answer}
              content={answerContent}
              progressMessage={progressMessage}
            />
            <Divider />
          </Stack>
        )}
        <ListBox aria-label={TRANSLATED_SEARCH_RESULTS} onSelectionChange={reset}>
          {results.length === 0 && (
            <ListBox.Section title={TRANSLATED_NO_RESULTS_FOUND}>
              <ListBox.Item key="No Results">{TRANSLATED_NO_RESULTS_FOUND}</ListBox.Item>
            </ListBox.Section>
          )}
          {Object.entries(orderedResults).map(([sectionName, sectionResults]) => (
            <ListBox.Section key={sectionName} title={sectionName}>
              {sectionResults}
            </ListBox.Section>
          ))}
        </ListBox>
      </Popover>
    </div>
  );
};
